
import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, Brain, Database, Server, Cpu, BarChart2, Camera } from 'lucide-react';
import AnimatedElement from '../utils/AnimatedElement';
import { motion } from 'framer-motion';

const services = [
  {
    title: 'Data Analytics',
    description: 'Real-time dashboards, ETL pipelines, and data warehouses. We process terabytes daily for clients in retail, manufacturing, and finance.',
    icon: <BarChart2 className="h-8 w-8" />,
    color: 'bg-blue-50 text-blue-500 dark:bg-blue-900/20 dark:text-blue-400',
    link: '/services/data-analytics',
  },
  {
    title: 'Machine Learning',
    description: 'Custom models for demand forecasting, fraud detection, and recommendation systems. Deployed in production with MLOps pipelines.',
    icon: <Brain className="h-8 w-8" />,
    color: 'bg-purple-50 text-purple-500 dark:bg-purple-900/20 dark:text-purple-400',
    link: '/services/machine-learning',
  },
  {
    title: 'IoT Solutions',
    description: 'Industrial IoT networks, sensor data processing, and edge computing. We connect factories, warehouses, and supply chains.',
    icon: <Cpu className="h-8 w-8" />,
    color: 'bg-green-50 text-green-500 dark:bg-green-900/20 dark:text-green-400',
    link: '/services/iot-solutions',
  },
  {
    title: 'AI Infrastructure',
    description: 'GPU clusters, model serving platforms, and distributed training systems. Built for scale and reliability.',
    icon: <Database className="h-8 w-8" />,
    color: 'bg-primary/10 text-primary dark:bg-primary/20 dark:text-primary',
    link: '/services/ai-infrastructure',
  },
  {
    title: 'Cloud Infrastructure',
    description: 'Multi-cloud architectures, serverless systems, and container orchestration. AWS, GCP, Azure deployments.',
    icon: <Server className="h-8 w-8" />,
    color: 'bg-cyan-50 text-cyan-500 dark:bg-cyan-900/20 dark:text-cyan-400',
    link: '/services/cloud-infrastructure',
  },
  {
    title: 'Computer Vision',
    description: 'Quality inspection systems, object detection, and image classification. Used in manufacturing and logistics.',
    icon: <Camera className="h-8 w-8" />,
    color: 'bg-rose-50 text-rose-500 dark:bg-rose-900/20 dark:text-rose-400',
    link: '/services/computer-vision',
  },
];

const ServicesSection = () => {
  return (
    <section className="py-24 bg-gray-50 dark:bg-gray-900 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-96 h-96 bg-blue-200/30 dark:bg-blue-800/10 rounded-full filter blur-3xl opacity-70"></div>
        <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-cyan-200/20 dark:bg-cyan-800/10 rounded-full filter blur-3xl opacity-60"></div>
      </div>

      <div className="container mx-auto px-6 relative z-10">
        <AnimatedElement animation="fadeIn">
          <div className="text-center mb-16">
            <span className="bg-primary/10 text-primary px-3 py-1 rounded-full text-sm font-medium">
              Our Solutions
            </span>
            <AnimatedElement animation="slideUp" delay={0.2}>
              <h2 className="text-3xl md:text-4xl font-bold mt-4 mb-6">
                What We Build
              </h2>
            </AnimatedElement>
            <AnimatedElement animation="slideUp" delay={0.3}>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                Production systems that process millions of records daily, ML models deployed at scale, 
                and IoT networks that connect factories across continents.
              </p>
            </AnimatedElement>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <AnimatedElement 
                key={service.title}
                animation="scale" 
                delay={0.1 + index * 0.05} // Reduced delay between items for mobile
                className="h-full"
              >
                <motion.div 
                  className="bg-white dark:bg-gray-800 rounded-2xl shadow-subtle border border-gray-100 dark:border-gray-700 p-6 hover:shadow-prominent transition-all duration-300 h-full group service-card"
                  whileHover={{ y: -5, scale: 1.01 }} // Reduced animation for better mobile experience
                  whileTap={{ scale: 0.98 }}
                  transition={{ type: "spring", stiffness: 300, damping: 15 }} // Adjusted for smoother mobile animation
                >
                  <motion.div 
                    className={`${service.color} p-3 rounded-xl inline-block mb-4 icon-container`}
                    initial={{ scale: 1 }}
                    whileInView={{ 
                      scale: [1, 1.1, 1], 
                      rotate: [0, 5, 0]
                    }}
                    transition={{ 
                      duration: 1.5, 
                      ease: "easeInOut",
                      delay: index * 0.1,
                      repeat: 0
                    }}
                    viewport={{ once: false, amount: 0.8 }}
                  >
                    {service.icon}
                  </motion.div>
                  <h3 className="text-xl font-semibold mb-3">{service.title}</h3>
                  <p className="text-muted-foreground mb-6">{service.description}</p>
                  <motion.div
                    whileHover={{ x: 3, y: -3 }}
                    transition={{ type: "spring", stiffness: 400, damping: 10 }}
                  >
                    <Link
                      to={service.link}
                      onClick={() => window.scrollTo(0, 0)} 
                      className="inline-flex items-center text-primary font-medium"
                    >
                      <span className="relative link-hover-effect">
                        Learn more
                      </span>
                      <ArrowUpRight className="ml-1 h-4 w-4 transform transition-transform duration-300" />
                    </Link>
                  </motion.div>
                </motion.div>
              </AnimatedElement>
            ))}
          </div>

          <AnimatedElement animation="slideUp" delay={0.6} className="mt-16 text-center">
            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              transition={{ type: "spring", stiffness: 400, damping: 10 }}
            >
              <Link
                to="/services"
                onClick={() => window.scrollTo(0, 0)}
                className="glass-button-primary px-6 py-3 text-base font-medium inline-block group btn-hover-glow"
              >
                View All Services
                <ArrowUpRight className="ml-2 h-4 w-4 inline-block transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
              </Link>
            </motion.div>
          </AnimatedElement>
        </AnimatedElement>
      </div>
    </section>
  );
};

export default ServicesSection;
